import { Button, Flex, HStack, Icon, Stack, useToast } from '@chakra-ui/react'
import { faUndo } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { AxiosError } from 'axios'
import { DateFormControl } from 'BrokerTsLib/src/components'
import type { ModelState } from 'BrokerTsLib/src/dtos'
import { BadRequestError } from 'BrokerTsLib/src/dtos'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { setModelStateErrors } from 'BrokerTsLib/src/Utils/formModelState'
import { useEffect, useRef } from 'react'
import * as yup from 'yup'

import type { PpzAuthorization, PpzAuthorizationUpdate } from '@/dtos'
import { useUpdate } from '@/hooks/usePpzAuthorization'

const schema = yup.object().shape({
  authorizationValidFrom: yup.date()
    .typeError('Zadejte datum')
    .required('Zadejte datum platnosti od'),
  authorizationValidTo: yup.date()
    .typeError('Zadejte datum')
    .required('Zadejte datum platnosti do')
    .min(yup.ref('authorizationValidFrom'), 'Platnost do musí být po platnosti od')
}) as any

export const PpzAuthorizationEditForm = (ppzAuthorization: PpzAuthorization) => {
  const toast = useToast()
  const form = useForm<PpzAuthorizationUpdate>({
    defaultValues: ppzAuthorization,
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
    mode: 'all'
  })
  const { handleSubmit, setError, formState: { isSubmitting, isDirty, isValid }, reset, watch, getValues, trigger } = form
  const formRef = useRef<HTMLFormElement>(null)

  const validFrom = watch('authorizationValidFrom')
  useEffect(() => {
    if (getValues('authorizationValidTo'))
      trigger('authorizationValidTo')
  }, [validFrom])

  const { mutateAsync: updateAsync } = useUpdate(ppzAuthorization.id)

  const submitHandler = async (data: PpzAuthorizationUpdate) => {
    await updateAsync(data)
      .then(() => {
        toast({
          title: 'Autorizace byla uložena.',
          status: 'success'
        })
      })
      .catch((e: AxiosError) => {
        if (e.response?.status === 400)
          setModelStateErrors(setError, new BadRequestError(e.response.data as ModelState<PpzAuthorizationUpdate>))
        else
          toast({
            title: 'Chyba při ukládání autorizace',
            status: 'error'
          })
      })
  }

  return (
    <FormProvider {...form}>
      <Stack as='form' ref={formRef} onSubmit={handleSubmit(submitHandler)} noValidate>
        <Flex gap={4}>
          <DateFormControl isRequired name="authorizationValidFrom" label="Platnost od" flex={1} />
          <DateFormControl isRequired name="authorizationValidTo" label="Platnost do" flex={1} />
        </Flex>

        <HStack w='full' justifyContent='space-between'>
          {isDirty && <Button colorScheme="gray" onClick={() => reset(ppzAuthorization)} p={2}><Icon as={FontAwesomeIcon} icon={faUndo} size='lg' /> Vrátit zpět</Button>}
          <Button type="submit" colorScheme="primary" isDisabled={!isValid || !isDirty} isLoading={isSubmitting} ml='auto'>Uložit</Button>
        </HStack>
      </Stack>
    </FormProvider>
  )
}